import {
	FontIconName,
	Icon,
	IconButton,
	Space,
	Typography
} from "client/Components";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useRef
} from "react";
import styles from "./SectionGrid.module.less";
import { useCopyToClipboard } from "client/Hooks";

export const SectionGrid = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.root}>
			{children}
		</div>
	);
};
SectionGrid.displayName = "SectionGrid";

type RowProps = {
	icon?: FontIconName;
	label: string;
	trackingId: string;
};

const Row = (props: PropsWithChildren<RowProps>): ReactElement => {
	const {
		icon,
		label,
		children,
		trackingId
	} = props;

	return (
		<div className={styles.item}>
			<div className={styles.iconContainer}>
				{icon && (
					<Icon
						value={icon}
						size="extra-large"
					/>
				)}
			</div>
			<div className={styles.itemContent}>
				<div
					{...setTrackingId(trackingId, "__label")}
					className={styles.label}
				>
					{label}
				</div>
				<div
					{...setTrackingId(trackingId, "__content")}
				>
					{children}
				</div>
			</div>
		</div>
	);
};
Row.displayName = "SectionGrid.Row";

export type SectionGridValueProps = {
	label?: string;
	trackingId?: string;
	valueToCopy?: string | null;
};

const Value = (props: PropsWithChildren<SectionGridValueProps>): ReactElement => {
	const {
		label,
		trackingId,
		children,
		valueToCopy
	} = props;

	return (
		<div className={styles.valueWrapper}>
			<div className={styles.value}>
				{isNotNil(label) && (
					<>
						<Typography>{label}:</Typography>
						<Space />
					</>
				)}
				<span
					{...setTrackingId(trackingId)}
				>
					{children}
				</span>
			</div>
			{isNotNil(valueToCopy) && (
				<div className={styles.valueCopy}>
					<CopyButton valueToCopy={valueToCopy} />
				</div>
			)}
		</div>
	);
};
Value.displayName = "SectionGrid.Value";

SectionGrid.Row = Row;
SectionGrid.Value = Value;

type CopyButtonProps = {
	valueToCopy: string;
};

const CopyButton = (props: CopyButtonProps): ReactElement => {
	const {
		valueToCopy
	} = props;

	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	useCopyToClipboard(
		ref,
		valueToCopy ?? ""
	);

	return (
		<IconButton
			icon="copy"
			kind="transparent"
			trackingId="copy"
			ref={ref}
			shape="round"
		/>
	);
};
CopyButton.displayName = "CopyButton";